/**
 * Baustein-Gruppen (Zubuchoptionen und Bündel) — gemeinsame Typen und Preislogik.
 *
 * Die Berechnung spiegelt `ContractOptionGroupApplicationService` im Backend. Sie dient hier
 * ausschließlich der Anzeige: maßgeblich bleibt der Server, der die Auswahl ohnehin erneut prüft
 * und den Bündelfaktor selbst bildet. Damit Kunde und Beleg dieselbe Zahl sehen, wird sie nach
 * derselben Regel gebildet und nicht frei geschätzt.
 */

const PERCENT_SCALE = 100;
/** Faktoren des Bündelvorteils werden auf sechs Nachkommastellen gerundet. */
const FACTOR_ROUNDING_SCALE = 1e6;

export const OPTION_GROUP_KIND_BUNDLE = 0;
export const OPTION_GROUP_KIND_CHOICE = 1;

export const OPTION_GROUP_PRICE_SUM_OF_ITEMS = 0;
export const OPTION_GROUP_PRICE_SET_TOTAL = 1;
export const OPTION_GROUP_PRICE_PERCENT_OFF = 2;
export const OPTION_GROUP_PRICE_FIXED_OFF = 3;

export const OPTION_GROUP_MODE_INCLUDED = 0;
export const OPTION_GROUP_MODE_OPTIONAL = 1;

export type ContractOptionGroupItemDto = {
  contractTemplateId: string;
  contractTemplateName?: string | null;
  value?: number | null;
  isActive: boolean;
};

export type ContractOptionGroupPriceTierDto = {
  fromCount: number;
  priceMode: number;
  priceValue: number;
};

export type ContractOptionGroupDto = {
  id: string;
  name: string;
  studioIds?: string[];
  isActive: boolean;
  // Vermarktung steht am Leistungsbündel des Erlebnisformulars; hier zählt der interne Name.
  selectionKind: number;
  minSelect?: number | null;
  maxSelect?: number | null;
  priceMode: number;
  priceValue?: number | null;
  items: ContractOptionGroupItemDto[];
  priceTiers: ContractOptionGroupPriceTierDto[];
  /** Vertragspaket-Vorlagen, die dieses Bündel führen. */
  usedInPackageCount?: number;
};

/** Zuordnung einer Gruppe zu einer Paketvorlage (aus `/api/v1/contract-package-templates`). */
export type ContractPackageTemplateOptionGroupDto = {
  contractOptionGroupId: string;
  name: string;
  selectionKind: number;
  selectionMode: number;
  defaultSelected: boolean;
  sortOrder: number;
  priceValueOverride?: number | null;
  isActive: boolean;
};

/** Gewählte Bausteine je Gruppe, Schlüssel ist die Gruppen-ID. */
export type OptionGroupSelectionState = Record<string, string[]>;

export function activeOptionGroupItems(group: ContractOptionGroupDto): ContractOptionGroupItemDto[] {
  return (group.items || []).filter((i) => i.isActive !== false);
}

/** Listenpreis der gewählten Bausteine — Gruppenpreis des Bausteins vor Vorlagen-Default. */
export function optionGroupListTotal(
  group: ContractOptionGroupDto,
  selectedTemplateIds: string[],
  defaultPriceOf: (contractTemplateId: string) => number | null | undefined
): number {
  const selected = new Set(selectedTemplateIds.map(String));
  return activeOptionGroupItems(group)
    .filter((i) => selected.has(String(i.contractTemplateId)))
    .reduce((sum, i) => sum + Number(i.value ?? defaultPriceOf(String(i.contractTemplateId)) ?? 0), 0);
}

/**
 * Anteil, auf den der Listenpreis fällt. 1 = kein Vorteil. Eine Mengenstaffel schlägt die
 * Grundpreisbildung der Gruppe, ein paketspezifischer Override schlägt beides.
 */
export function optionGroupBundleFactor(
  group: ContractOptionGroupDto,
  priceValueOverride: number | null | undefined,
  selectedCount: number,
  listTotal: number
): number {
  if (!(listTotal > 0)) return 1;

  let mode = Number(group.priceMode ?? OPTION_GROUP_PRICE_SUM_OF_ITEMS);
  let value: number | null = priceValueOverride ?? group.priceValue ?? null;

  const tier = (group.priceTiers || [])
    .filter((t) => selectedCount >= Number(t.fromCount))
    .sort((a, b) => Number(b.fromCount) - Number(a.fromCount))[0];
  if (tier && priceValueOverride == null) {
    mode = Number(tier.priceMode);
    value = Number(tier.priceValue);
  }

  if (value == null) return 1;

  let target: number | null = null;
  if (mode === OPTION_GROUP_PRICE_SET_TOTAL) target = value;
  else if (mode === OPTION_GROUP_PRICE_PERCENT_OFF) target = listTotal * (1 - value / PERCENT_SCALE);
  else if (mode === OPTION_GROUP_PRICE_FIXED_OFF) target = listTotal - value;

  if (target == null) return 1;
  if (target < 0) target = 0;
  if (target >= listTotal) return 1;

  return Math.round((target / listTotal) * FACTOR_ROUNDING_SCALE) / FACTOR_ROUNDING_SCALE;
}

/** Gruppenpreis nach Vorteil — das, was der Kunde für die Auswahl zahlt. */
export function optionGroupEffectivePrice(listTotal: number, factor: number): number {
  return Math.round(listTotal * factor * PERCENT_SCALE) / PERCENT_SCALE;
}

/** Baut die Vorauswahl aus der Paketvorlage — `Included` immer, `DefaultSelected` als Vorschlag. */
export function buildDefaultOptionGroupSelection(
  usages: ContractPackageTemplateOptionGroupDto[],
  groups: ContractOptionGroupDto[]
): OptionGroupSelectionState {
  const groupById = new Map(groups.map((g) => [String(g.id), g]));
  const next: OptionGroupSelectionState = {};
  for (const usage of usages || []) {
    if (usage.isActive === false) continue;
    const group = groupById.get(String(usage.contractOptionGroupId));
    if (!group || group.isActive === false) continue;
    const items = activeOptionGroupItems(group);
    const included = Number(usage.selectionMode) === OPTION_GROUP_MODE_INCLUDED;
    if (!included && !usage.defaultSelected) {
      next[String(usage.contractOptionGroupId)] = [];
      continue;
    }
    // Bei einer Auswahl ist „vorausgewählt" nur bis zur Höchstzahl sinnvoll.
    const max = Number(group.selectionKind) === OPTION_GROUP_KIND_CHOICE && group.maxSelect != null
      ? Number(group.maxSelect)
      : items.length;
    next[String(usage.contractOptionGroupId)] = items.slice(0, max).map((i) => String(i.contractTemplateId));
  }
  return next;
}

/** Sichtbare Vorprüfung; verbindlich bleibt die serverseitige Prüfung im Vertragskern. */
export function validateOptionGroupSelection(
  usages: ContractPackageTemplateOptionGroupDto[],
  groups: ContractOptionGroupDto[],
  selection: OptionGroupSelectionState,
  text: (key: string) => string
): string | null {
  const groupById = new Map(groups.map((g) => [String(g.id), g]));
  for (const usage of usages || []) {
    if (usage.isActive === false) continue;
    const group = groupById.get(String(usage.contractOptionGroupId));
    if (!group || group.isActive === false) continue;
    if (Number(usage.selectionMode) === OPTION_GROUP_MODE_INCLUDED) continue;
    if (Number(group.selectionKind) !== OPTION_GROUP_KIND_CHOICE) continue;

    const chosen = selection[String(usage.contractOptionGroupId)] || [];
    if (group.minSelect != null && chosen.length > 0 && chosen.length < Number(group.minSelect)) {
      return text("Select at least {0}").replace("{0}", String(group.minSelect));
    }
    if (group.maxSelect != null && chosen.length > Number(group.maxSelect)) {
      return text("Select at most {0}").replace("{0}", String(group.maxSelect));
    }
  }
  return null;
}

/** Payload für `POST /api/v1/contract-packages` — leere Gruppen fallen weg. */
export function toOptionGroupPayload(selection: OptionGroupSelectionState) {
  return Object.entries(selection || {})
    .filter(([, ids]) => (ids || []).length > 0)
    .map(([contractOptionGroupId, contractTemplateIds]) => ({
      contractOptionGroupId,
      contractTemplateIds: contractTemplateIds.map(String)
    }));
}
